import { defineStore } from 'pinia';

import type { SMSTemplateSchema } from '@stuut/api-sdk/models';

import { sdk } from '@/sdk';

interface State {
  initLoad: boolean;
  loading: boolean;
  templates: SMSTemplateSchema[];
  totalTemplates: number;
}

export const useSmsTemplates = defineStore('smsTemplates', {
  actions: {
    addTemplatesToState(templates: SMSTemplateSchema[]) {
      this.templates.push(...templates);
    },

    async fetchSmsTemplates() {
      this.loading = true;
      try {
        const response = await sdk.smsTemplates.list({ limit: 200 });
        this.templates = response.data ?? [];
        this.totalTemplates = response.totalRecords ?? 0;
        return this.templates;
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    removeTemplatesFromState(templates: SMSTemplateSchema[]) {
      this.templates = this.templates.filter(
        (t) => !templates.some((template) => template.id === t.id),
      );
    },

    setNewDefaultTemplate(template: SMSTemplateSchema) {
      this.templates.forEach((t) => {
        t.isDefault = t.id === template.id;
      });
    },

    async setupSmsTemplates() {
      if (this.templates.length || this.initLoad) {
        return this.templates;
      }
      this.initLoad = true;
      return this.fetchSmsTemplates();
    },

    updateTemplatesInState(templates: SMSTemplateSchema[]) {
      templates.forEach((updatedTemplate) => {
        const index = this.templates.findIndex((t) => t.id === updatedTemplate.id);
        if (index !== -1) {
          this.templates[index] = updatedTemplate;
        }
      });
    },
  },
  getters: {
    getById: (state) => {
      return (templateId: string) =>
        state.templates.find((template) => template.id === templateId) as
          | SMSTemplateSchema
          | undefined;
    },
  },
  state: (): State => ({
    initLoad: false,
    loading: false,
    templates: [],
    totalTemplates: 0,
  }),
});

export default useSmsTemplates;
